import React, { useState } from 'react';
import { FaPlus, FaClock, FaExchangeAlt } from 'react-icons/fa';
import { Select, Button, message, Dropdown, Menu } from 'antd';
import TimetableEntry from './TimetableEntry';

const DayColumn = ({
  day,
  entries,
  weekTemplate,
  subjects,
  labBatches,
  onAddEntry,
  onUpdateEntry,
  onRemoveEntry,
  onSubjectChange,
  getSubjectName,
  tenantId,
  selectedDepartmentId,
  selectedAcademicYearId,
  selectedAcademicYear,
  selectedSemesterId,
  selectedClassId,
  electiveGroups,
  loadingElectives,
  slotTemplates = [],
  onApplySlotTemplate,
  applyingSlotTemplate
}) => {
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);
  const hasEntries = entries && entries.length > 0;

  const handleApplyTemplate = (mode) => {
    if (!selectedTemplateId) {
      message.warning('Please select a time slot template');
      return;
    }
    const replaceMode = mode === 'replace';
    onApplySlotTemplate(day, selectedTemplateId, replaceMode);
    if (!replaceMode) {
      setSelectedTemplateId(null);
    }
  };

  const menu = (
    <Menu>
      <Menu.Item key="add" onClick={() => handleApplyTemplate('add')}>
        <div className="flex items-center gap-2">
          <FaPlus className="text-green-600" />
          <span>Add Missing Slots Only</span>
          <span className="text-xs text-gray-500">(Preserve existing)</span>
        </div>
      </Menu.Item>
      <Menu.Item key="replace" onClick={() => handleApplyTemplate('replace')}>
        <div className="flex items-center gap-2">
          <FaExchangeAlt className="text-orange-600" />
          <span>Replace All Slots</span>
          <span className="text-xs text-gray-500">(Remove existing)</span>
        </div>
      </Menu.Item>
    </Menu>
  );

  return (
    <div className="flex flex-col h-full max-h-[85vh] bg-gray-50 border border-gray-200 rounded-xl shadow-sm overflow-hidden">
      {/* Sticky Header */}
      <div className="flex-none p-3 bg-white border-b border-gray-200 z-10 shadow-sm">
        <div className="flex justify-between items-center mb-2">
          <h4 className="font-bold text-gray-700 text-sm uppercase tracking-wide">{day}</h4>
          <button
            onClick={() => {
              if (typeof onAddEntry === 'function') onAddEntry(day);
            }}
            className="flex items-center justify-center w-7 h-7 bg-blue-50 text-blue-600 rounded-full hover:bg-blue-600 hover:text-white transition-all shadow-sm group"
            title="Add Session"
          >
            <FaPlus size={10} className="transform group-hover:rotate-90 transition-transform" />
          </button>
        </div>
        
        {/* Time Slot Template Selector - Always visible when templates exist */}
        {slotTemplates.length > 0 && (
          <div className="flex gap-2 mt-2">
            <Select
              showSearch
              placeholder="Select time slot template..."
              value={selectedTemplateId || undefined}
              onChange={setSelectedTemplateId}
              style={{ flex: 1 }}
              size="small"
              optionFilterProp="children"
              allowClear
            >
              {slotTemplates.map(tpl => (
                <Select.Option key={tpl._id} value={tpl._id} title={tpl.name}>
                  {tpl.name} ({tpl.slots?.length || 0} slots)
                </Select.Option>
              ))}
            </Select>
            <Dropdown overlay={menu} disabled={!selectedTemplateId || applyingSlotTemplate}>
              <Button
                size="small"
                type="primary"
                icon={<FaClock />}
                loading={applyingSlotTemplate}
                disabled={!selectedTemplateId}
              >
                Apply Template
              </Button>
            </Dropdown>
          </div>
        )}
        
        {/* Message when no templates exist */}
        {slotTemplates.length === 0 && (
          <div className="text-xs text-gray-400 text-center mt-2">
            No time slot templates available. Create one first.
          </div>
        )}
        
        {/* Indicator for existing entries */}
        {hasEntries && (
          <div className="text-xs text-gray-500 mt-2 flex items-center gap-1">
            <span className="inline-block w-2 h-2 bg-green-500 rounded-full"></span>
            {entries.length} existing slot(s)
          </div>
        )}
      </div>

      {/* Scrollable Body */}
      <div className="flex-1 overflow-y-auto p-2 space-y-2 custom-scrollbar">
        {entries.map((entry, idx) => {
          const globalIndex = weekTemplate.findIndex(e => e === entry);
          const renderKey = entry._id || entry.id || globalIndex;

          return (
            <TimetableEntry
              key={renderKey}
              entry={entry}
              index={globalIndex}
              subjects={subjects}
              labBatches={labBatches}
              onUpdate={onUpdateEntry}
              onRemove={onRemoveEntry}
              onSubjectChange={onSubjectChange}
              getSubjectName={getSubjectName}
              tenantId={tenantId}
              departmentId={selectedDepartmentId}
              academicYearId={selectedAcademicYearId}
              isSemesterBased={selectedAcademicYear?.isSemesterBased || false}
              semesterId={selectedSemesterId}
              classId={selectedClassId}
              electiveGroups={electiveGroups}
              loadingElectiveGroups={loadingElectives}
            />
          );
        })}

        {/* Empty State */}
        {entries.length === 0 && (
          <div className="flex flex-col items-center justify-center h-24 text-gray-400 border-2 border-dashed border-gray-200 rounded-lg bg-gray-50/50 mt-2">
            <span className="text-xs">No sessions</span>
            <button 
              onClick={() => onAddEntry && onAddEntry(day)}
              className="mt-1 text-[10px] text-blue-500 hover:underline font-medium"
            >
              Add one
            </button>
          </div>
        )}
        
        <div className="h-6"></div>
      </div>
    </div>
  );
};

export default DayColumn;